import { api, tagTypes } from "./api";

// API Response wrapper types that match the actual API response
export interface ApiResponse<T> {
	success: boolean;
	message: string;
	payload: {
		data: T;
	};
}

// Wallet data types that match the actual API response structure
export interface WalletData {
	balance: number;
	transactions?: any[];
}

export interface Transaction {
	id: string;
	amount: number;
	type: "credit" | "debit";
	description: string;
	status: "pending" | "completed" | "failed";
	timestamp: string;
	reference?: string;
}

export interface ArtisanWalletData extends WalletData {
	artisanId?: string;
	artisanName?: string;
	serviceCategory?: string;
}

export interface CustomerWalletData extends WalletData {
	customerId?: string;
	customerName?: string;
}

export interface BookingWalletData extends WalletData {
	bookingId?: string;
	serviceType?: string;
	escrow_balance_amount?: number;
	commitment_fee_commission?: number;
	service_fee_commission?: number;
	verification_fee_amount?: number;
}

// Legacy types for backward compatibility with existing components
export interface CustomerWalletTrnxTypes {
	customer: string;
	id: string;
	amount: number;
	created_at: string;
	type: string;
	img: string;
	transaction_details: {
		[x: string]: any;
	};
}

export interface VendorWalletTrnxTypes {
	vendor: string;
	id: string;
	amount: number;
	created_at: string;
	type: string;
	img: string;
	status: string;
	transaction_details: {
		[x: string]: any;
	};
}

export interface BookingsTypes {
	_id: string;
	amount: number;
	status: string;
	narration: string;
	createdAt: string;
}

export const walletApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Artisan wallet endpoints
		getArtisanWalletData: builder.query<ApiResponse<ArtisanWalletData>, void>({
			query: () => ({ url: "artisan/v1/dashboard/wallet", method: "GET" }),
			providesTags: [tagTypes.Wallet],
		}),
		getArtisanTransactions: builder.query<
			ApiResponse<VendorWalletTrnxTypes[]>,
			void
		>({
			query: () => ({
				url: "artisan/v1/wallet/transactions/recent",
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),
		getArtisanTransactionHistory: builder.query<
			ApiResponse<VendorWalletTrnxTypes[]>,
			{ page?: number; limit?: number }
		>({
			query: ({ page = 1, limit = 20 }) => ({
				url: `artisan/v1/wallet/transactions?page=${page}&limit=${limit}`,
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),

		// Customer wallet endpoints
		getCustomerWalletData: builder.query<ApiResponse<CustomerWalletData>, void>(
			{
				query: () => ({ url: "user/v1/dashboard/wallet", method: "GET" }),
				providesTags: [tagTypes.Wallet],
			}
		),
		getCustomerTransactions: builder.query<
			ApiResponse<CustomerWalletTrnxTypes[]>,
			void
		>({
			query: () => ({
				url: "user/v1/wallet/transactions/recent",
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),
		getCustomerTransactionHistory: builder.query<
			ApiResponse<CustomerWalletTrnxTypes[]>,
			{ page?: number; limit?: number }
		>({
			query: ({ page = 1, limit = 20 }) => ({
				url: `user/v1/wallet/transactions?page=${page}&limit=${limit}`,
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),

		// Booking wallet endpoints
		getBookingWalletData: builder.query<ApiResponse<BookingWalletData>, void>({
			query: () => ({
				url: "booking/v1/admin/dashboard/wallet",
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet],
		}),
		getBookingTransactions: builder.query<
			ApiResponse<{ transactions: BookingsTypes[] }>,
			void
		>({
			query: () => ({ url: "booking/v1/admin/dashboard/trx", method: "GET" }),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),
		getBookingTransactionHistory: builder.query<
			ApiResponse<BookingsTypes[]>,
			{ page?: number; limit?: number }
		>({
			query: ({ page = 1, limit = 20 }) => ({
				url: `booking/v1/admin/dashboard/transactions?page=${page}&limit=${limit}`,
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet, tagTypes.Transaction],
		}),

		// Admin wallet overview
		getAdminWalletOverview: builder.query<
			ApiResponse<{
				totalArtisanBalance: number;
				totalCustomerBalance: number;
				totalBookingBalance: number;
				currency: string;
			}>,
			void
		>({
			query: () => ({ url: "admin/v1/wallet/overview", method: "GET" }),
			providesTags: [tagTypes.Wallet],
		}),

		// Export wallet data
		exportWalletData: builder.query<
			any,
			{ type: "artisan" | "customer" | "booking"; format?: "csv" | "excel" }
		>({
			query: ({ type, format = "csv" }) => ({
				url: `admin/v1/wallet/export/${type}?format=${format}`,
				method: "GET",
			}),
			providesTags: [tagTypes.Wallet],
		}),
	}),
});

export const {
	useGetArtisanWalletDataQuery,
	useGetArtisanTransactionsQuery,
	useGetArtisanTransactionHistoryQuery,
	useGetCustomerWalletDataQuery,
	useGetCustomerTransactionsQuery,
	useGetCustomerTransactionHistoryQuery,
	useGetBookingWalletDataQuery,
	useGetBookingTransactionsQuery,
	useGetBookingTransactionHistoryQuery,
	useGetAdminWalletOverviewQuery,
	useExportWalletDataQuery,
	useLazyExportWalletDataQuery,
} = walletApi;
